/**
 * CustomFieldsManager - Complete Registration Form Builder
 * Allows customization of both built-in fields (name, email, etc.) and custom fields
 */
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { customFieldsAPI, eventAPI, getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { EventFormBuilder } from '@/components/event_admin/EventFormBuilder';
import { ExternalFormConnector } from '@/components/event_admin/ExternalFormConnector';
import {
  Plus, Trash2, Edit2, GripVertical, Save, X, ChevronDown, ChevronUp,
  Type, List, CircleDot, CheckSquare, Hash, Mail, Phone, Calendar,
  AlignLeft, Loader2, Info, Eye, EyeOff, User, Building, MapPin,
  CreditCard, Globe, Settings2
} from 'lucide-react';

// Built-in registration fields that can be toggled
const BUILT_IN_FIELDS = [
  { id: 'name', label: 'Full Name', icon: User, required: true, canDisable: false },
  { id: 'email', label: 'Email Address', icon: Mail, required: true, canDisable: false },
  { id: 'phone', label: 'Phone Number', icon: Phone, required: false, canDisable: true },
  { id: 'company', label: 'Company/Organization', icon: Building, required: false, canDisable: true },
  { id: 'job_title', label: 'Job Title', icon: CreditCard, required: false, canDisable: true },
  { id: 'country', label: 'Country', icon: Globe, required: false, canDisable: true },
  { id: 'city', label: 'City', icon: MapPin, required: false, canDisable: true },
  { id: 'dietary_requirements', label: 'Dietary Requirements', icon: List, required: false, canDisable: true },
  { id: 'special_needs', label: 'Accessibility/Special Needs', icon: Info, required: false, canDisable: true },
];

const CUSTOM_FIELD_TYPES = [
  { value: 'text', label: 'Short Text', icon: Type, description: 'Single line text input' },
  { value: 'textarea', label: 'Long Text', icon: AlignLeft, description: 'Multi-line text area' },
  { value: 'dropdown', label: 'Dropdown', icon: ChevronDown, description: 'Select from a list' },
  { value: 'single_choice', label: 'Single Choice', icon: CircleDot, description: 'Radio buttons' },
  { value: 'multi_choice', label: 'Multiple Choice', icon: CheckSquare, description: 'Checkboxes' },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare, description: 'Yes/No toggle' },
  { value: 'number', label: 'Number', icon: Hash, description: 'Numeric input' },
  { value: 'email', label: 'Email', icon: Mail, description: 'Email address' },
  { value: 'phone', label: 'Phone', icon: Phone, description: 'Phone number' },
  { value: 'date', label: 'Date', icon: Calendar, description: 'Date picker' },
];

const CustomFieldsManager = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('builtin');
  const [showDialog, setShowDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [formData, setFormData] = useState({
    label: '',
    field_type: 'text',
    required: false,
    placeholder: '',
    help_text: '',
    options: [],
  });
  const [newOption, setNewOption] = useState('');
  const [builtInConfig, setBuiltInConfig] = useState({});
  const [savingBuiltIn, setSavingBuiltIn] = useState(false);

  // Fetch event for built-in field config
  const { data: event } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => eventAPI.get(eventId).then(r => r.data),
    enabled: !!eventId,
  });

  // Initialize built-in config from event
  useEffect(() => {
    // iter 632 — the save writes to `settings.registration_form_config` (that
    // is what `eventAPI.update` posts below), but this read looked for it at
    // the top level, so the screen fell back to defaults on every reload even
    // once the write started landing.
    const saved = event?.settings?.registration_form_config || event?.registration_form_config;
    if (saved) {
      setBuiltInConfig(saved);
    } else {
      // Default config - all enabled, some required
      const defaultConfig = {};
      BUILT_IN_FIELDS.forEach(field => {
        defaultConfig[field.id] = {
          enabled: true,
          required: field.required,
          label: field.label,
        };
      });
      setBuiltInConfig(defaultConfig);
    }
  }, [event]);

  // Fetch custom fields
  const { data: fields = [], isLoading } = useQuery({
    queryKey: ['custom-fields', eventId],
    queryFn: () => customFieldsAPI.listAdmin(eventId).then(r => r.data),
    enabled: !!eventId,
  });

  // Create custom field mutation
  const createMutation = useMutation({
    mutationFn: (data) => customFieldsAPI.create(eventId, data),
    onSuccess: () => {
      toast.success('Custom field created');
      queryClient.invalidateQueries(['custom-fields', eventId]);
      handleCloseDialog();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, 'Failed to create field'));
    },
  });

  // Update custom field mutation
  const updateMutation = useMutation({
    mutationFn: ({ fieldId, data }) => customFieldsAPI.update(eventId, fieldId, data),
    onSuccess: () => {
      toast.success('Custom field updated');
      queryClient.invalidateQueries(['custom-fields', eventId]);
      handleCloseDialog();
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, 'Failed to update field'));
    },
  });

  // Delete custom field mutation
  const deleteMutation = useMutation({
    mutationFn: (fieldId) => customFieldsAPI.delete(eventId, fieldId),
    onSuccess: () => {
      toast.success('Custom field deleted');
      queryClient.invalidateQueries(['custom-fields', eventId]);
    },
    onError: (err) => {
      toast.error(getErrorMessage(err, 'Failed to delete field'));
    },
  });

  // Toggle custom field active mutation
  const toggleActiveMutation = useMutation({
    mutationFn: ({ fieldId, isActive }) => customFieldsAPI.update(eventId, fieldId, { is_active: isActive }),
    onSuccess: () => {
      queryClient.invalidateQueries(['custom-fields', eventId]);
    },
  });

  // Save built-in field configuration
  const handleSaveBuiltInConfig = async () => {
    setSavingBuiltIn(true);
    try {
      await eventAPI.update(eventId, { 
        settings: { 
          ...event?.settings,
          registration_form_config: builtInConfig 
        }
      });
      toast.success('Registration form configuration saved');
      queryClient.invalidateQueries(['event', eventId]);
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSavingBuiltIn(false);
    }
  };

  // Toggle built-in field
  const handleToggleBuiltIn = (fieldId, property, value) => {
    setBuiltInConfig(prev => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        [property]: value,
      }
    }));
  };

  // Update built-in field label
  const handleUpdateBuiltInLabel = (fieldId, newLabel) => {
    setBuiltInConfig(prev => ({
      ...prev,
      [fieldId]: {
        ...prev[fieldId],
        label: newLabel,
      }
    }));
  };

  const handleOpenDialog = (field = null) => {
    if (field) {
      setEditingField(field);
      setFormData({
        label: field.label || '',
        field_type: field.field_type || 'text',
        required: field.required || false,
        placeholder: field.placeholder || '',
        help_text: field.help_text || '',
        options: field.options || [],
      });
    } else {
      setEditingField(null);
      setFormData({
        label: '',
        field_type: 'text',
        required: false,
        placeholder: '',
        help_text: '',
        options: [],
      });
    }
    setNewOption('');
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingField(null);
    setFormData({
      label: '',
      field_type: 'text',
      required: false,
      placeholder: '',
      help_text: '',
      options: [],
    });
    setNewOption('');
  };

  const handleAddOption = () => {
    if (!newOption.trim()) return;
    const option = {
      label: newOption.trim(),
      value: newOption.trim().toLowerCase().replace(/\s+/g, '_'),
    };
    setFormData(prev => ({
      ...prev,
      options: [...prev.options, option],
    }));
    setNewOption('');
  };

  const handleRemoveOption = (index) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = () => {
    if (!formData.label.trim()) {
      toast.error('Please enter a field label');
      return;
    }

    const needsOptions = ['dropdown', 'single_choice', 'multi_choice'].includes(formData.field_type);
    if (needsOptions && formData.options.length < 2) {
      toast.error('Please add at least 2 options');
      return;
    }

    const data = {
      ...formData,
      options: needsOptions ? formData.options : [],
    };

    if (editingField) {
      updateMutation.mutate({ fieldId: editingField.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const needsOptions = ['dropdown', 'single_choice', 'multi_choice'].includes(formData.field_type);
  const currentFieldType = CUSTOM_FIELD_TYPES.find(t => t.value === formData.field_type);

  return (
    <div className="space-y-6" data-testid="custom-fields-manager">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Settings2 className="h-6 w-6" />
            Registration Form Builder
          </h2>
          <p className="text-slate-500 mt-1">
            Customize the registration form for your event
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
          <TabsTrigger value="builtin" className="flex items-center gap-2">
            <User className="h-4 w-4" />
            Standard Fields
          </TabsTrigger>
          <TabsTrigger value="builder" className="flex items-center gap-2" data-testid="tab-form-builder">
            <Settings2 className="h-4 w-4" />
            Form Builder
          </TabsTrigger>
          <TabsTrigger value="google-forms" className="flex items-center gap-2" data-testid="tab-google-forms">
            <Globe className="h-4 w-4" />
            Google Forms
          </TabsTrigger>
          <TabsTrigger value="custom" className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Custom Fields (legacy)
          </TabsTrigger>
        </TabsList>

        {/* Form Builder Tab — new drag-and-drop UI for Phase A */}
        <TabsContent value="builder" className="space-y-4">
          <EventFormBuilder eventId={eventId} />
        </TabsContent>

        {/* Google Forms Tab — paste a public Google Form URL */}
        <TabsContent value="google-forms" className="space-y-4">
          <ExternalFormConnector eventId={eventId} />
        </TabsContent>

        {/* Built-in Fields Tab */}
        <TabsContent value="builtin" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Standard Registration Fields</CardTitle>
              <CardDescription>
                Enable or disable standard fields and customize their labels
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {BUILT_IN_FIELDS.map((field) => {
                const config = builtInConfig[field.id] || { enabled: true, required: field.required, label: field.label };
                const FieldIcon = field.icon;
                
                return (
                  <div 
                    key={field.id} 
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      config.enabled ? 'bg-white' : 'bg-slate-50 opacity-60'
                    }`}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className={`p-2 rounded-lg ${config.enabled ? 'bg-indigo-100 text-indigo-600' : 'bg-slate-100 text-slate-400'}`}>
                        <FieldIcon className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <Input
                          value={config.label || field.label}
                          onChange={(e) => handleUpdateBuiltInLabel(field.id, e.target.value)}
                          disabled={!config.enabled}
                          className="font-medium border-0 p-0 h-auto text-base focus-visible:ring-0 bg-transparent"
                        />
                        <p className="text-sm text-slate-500 mt-0.5">
                          Field ID: {field.id}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-6">
                      {/* Required toggle */}
                      <div className="flex items-center gap-2">
                        <Label className="text-sm text-slate-500">Required</Label>
                        <Switch
                          checked={config.required}
                          onCheckedChange={(checked) => handleToggleBuiltIn(field.id, 'required', checked)}
                          disabled={!config.enabled || !field.canDisable}
                        />
                      </div>
                      
                      {/* Enable/Disable toggle */}
                      {field.canDisable ? (
                        <div className="flex items-center gap-2">
                          <Label className="text-sm text-slate-500">Show</Label>
                          <Switch
                            checked={config.enabled}
                            onCheckedChange={(checked) => handleToggleBuiltIn(field.id, 'enabled', checked)}
                          />
                        </div>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Always shown</Badge>
                      )}
                    </div>
                  </div>
                );
              })}
              
              <div className="pt-4 border-t">
                <Button onClick={handleSaveBuiltInConfig} disabled={savingBuiltIn}>
                  {savingBuiltIn ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Configuration
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Fields Tab */}
        <TabsContent value="custom" className="space-y-4">
          <div className="flex justify-between items-center">
            <p className="text-sm text-slate-500">
              Add custom questions to collect additional information
            </p>
            <Button onClick={() => handleOpenDialog()} data-testid="add-custom-field-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Custom Field
            </Button>
          </div>

          {isLoading ? (
            <Card>
              <CardContent className="py-8 flex justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </CardContent>
            </Card>
          ) : fields.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <List className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <h3 className="font-medium text-slate-900">No custom fields yet</h3>
                <p className="text-sm text-slate-500 mt-1">
                  Add custom questions to your registration form
                </p>
                <Button className="mt-4" onClick={() => handleOpenDialog()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Field
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {fields.map((field, index) => {
                const fieldType = CUSTOM_FIELD_TYPES.find(t => t.value === field.field_type);
                const FieldIcon = fieldType?.icon || Type;
                
                return (
                  <Card key={field.id} className={!field.is_active ? 'opacity-60' : ''}>
                    <CardContent className="py-4">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <FieldIcon className="h-5 w-5 text-slate-600" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{field.label}</h4>
                            {field.required && (
                              <Badge variant="destructive" className="text-xs">Required</Badge>
                            )}
                            <Badge variant="outline" className="text-xs">
                              {fieldType?.label || field.field_type}
                            </Badge>
                          </div>
                          {field.help_text && (
                            <p className="text-sm text-slate-500 mt-0.5 truncate">{field.help_text}</p>
                          )}
                          {field.options?.length > 0 && (
                            <p className="text-xs text-slate-400 mt-1">
                              {field.options.length} options
                            </p>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => toggleActiveMutation.mutate({ 
                              fieldId: field.id, 
                              isActive: !field.is_active 
                            })}
                            title={field.is_active ? 'Hide field' : 'Show field'}
                          >
                            {field.is_active ? (
                              <Eye className="h-4 w-4" />
                            ) : (
                              <EyeOff className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenDialog(field)}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            onClick={() => {
                              if (confirm('Delete this field?')) {
                                deleteMutation.mutate(field.id);
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Form Preview */}
      <Card className="border-dashed border-2">
        <CardHeader>
          <CardTitle className="text-sm flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Form Preview
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Built-in fields preview */}
          {BUILT_IN_FIELDS.filter(f => builtInConfig[f.id]?.enabled !== false).map(field => {
            const config = builtInConfig[field.id] || {};
            return (
              <div key={field.id} className="space-y-1">
                <Label className="text-sm">
                  {config.label || field.label}
                  {config.required && <span className="text-red-500 ml-1">*</span>}
                </Label>
                <Input placeholder={`Enter ${(config.label || field.label).toLowerCase()}`} disabled />
              </div>
            );
          })}
          
          {/* Custom fields preview */}
          {fields.filter(f => f.is_active).map(field => (
            <div key={field.id} className="space-y-1">
              <Label className="text-sm">
                {field.label}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </Label>
              {field.field_type === 'textarea' ? (
                <Textarea placeholder={field.placeholder} disabled />
              ) : field.field_type === 'dropdown' || field.field_type === 'single_choice' ? (
                <Select disabled>
                  <SelectTrigger>
                    <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                  </SelectTrigger>
                </Select>
              ) : field.field_type === 'checkbox' ? (
                <div className="flex items-center gap-2">
                  <Switch disabled />
                  <span className="text-sm text-slate-500">{field.help_text || 'Yes / No'}</span>
                </div>
              ) : (
                <Input 
                  type={field.field_type === 'number' ? 'number' : field.field_type === 'date' ? 'date' : 'text'} 
                  placeholder={field.placeholder} 
                  disabled 
                />
              )}
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Add/Edit Custom Field Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingField ? 'Edit Custom Field' : 'Add Custom Field'}
            </DialogTitle>
            <DialogDescription>
              {editingField 
                ? 'Update this custom registration field' 
                : 'Create a new custom question for your registration form'
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Field Type Selector */}
            <div className="space-y-2">
              <Label>Field Type</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {CUSTOM_FIELD_TYPES.slice(0, 6).map((type) => {
                  const TypeIcon = type.icon;
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, field_type: type.value }))}
                      className={`p-3 rounded-lg border-2 transition-all text-left ${
                        formData.field_type === type.value
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <TypeIcon className={`h-5 w-5 ${
                        formData.field_type === type.value ? 'text-indigo-600' : 'text-slate-400'
                      }`} />
                      <p className="text-sm font-medium mt-1">{type.label}</p>
                    </button>
                  );
                })}
              </div>
              {currentFieldType && (
                <p className="text-xs text-slate-500 mt-1">{currentFieldType.description}</p>
              )}
            </div>
            
            {/* Field Label */}
            <div className="space-y-2">
              <Label>Label *</Label>
              <Input
                value={formData.label}
                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                placeholder="e.g., How did you hear about us?"
              />
            </div>
            
            {/* Options for dropdown/choice fields */}
            {needsOptions && (
              <div className="space-y-2">
                <Label>Options *</Label>
                <div className="space-y-2">
                  {formData.options.map((option, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input value={option.label} disabled className="flex-1" />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveOption(index)}
                        className="text-red-500"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2">
                    <Input
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      placeholder="Add an option..."
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption())}
                      className="flex-1"
                    />
                    <Button type="button" variant="outline" onClick={handleAddOption}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Placeholder */}
            <div className="space-y-2">
              <Label>Placeholder Text</Label>
              <Input
                value={formData.placeholder}
                onChange={(e) => setFormData(prev => ({ ...prev, placeholder: e.target.value }))}
                placeholder="Optional hint text..."
              />
            </div>
            
            {/* Help Text */}
            <div className="space-y-2">
              <Label>Help Text</Label>
              <Textarea
                value={formData.help_text}
                onChange={(e) => setFormData(prev => ({ ...prev, help_text: e.target.value }))}
                placeholder="Additional instructions for attendees..."
                rows={2}
              />
            </div>
            
            {/* Required Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label>Required Field</Label>
                <p className="text-sm text-slate-500">Make this field mandatory</p>
              </div>
              <Switch
                checked={formData.required}
                onCheckedChange={(checked) => setFormData(prev => ({ ...prev, required: checked }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={createMutation.isPending || updateMutation.isPending}
            >
              {(createMutation.isPending || updateMutation.isPending) && (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              )}
              {editingField ? 'Update Field' : 'Create Field'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomFieldsManager;
